/**
 * usePremiumPackages
 *
 * Feature hook for the Premium Packages page.
 * Loads the catalog from SecureStorage and reads user subscriptions reactively
 * from Redux via `useSelector` so the page re-renders when the live Firestore
 * listener (`PremiumPackagesListener`) updates the slice — matches Flutter's
 * `Consumer<PremiumPackagesNotifier>` pattern in
 * `lib/presentation/screens/premium/premium_packages_screen.dart:267-291`.
 *
 * UI callbacks are injected by the caller so the hook stays free of UI concerns.
 * Each callback returns a Promise, allowing the hook to await mid-process UI events
 * (e.g. pause for user confirmation before proceeding with IAP).
 */
import { useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { GetPremiumPackagesUseCase } from '@/domain/usecases/premiumPackage/getPremiumPackagesUseCase';
import type { PremiumPackageEntity } from '@/domain/entities/premiumPackage/premiumPackageEntity';
import type { InAppPurchaseStatus } from '@/domain/enums/premiumPackages/InAppPurchaseStatus';
import { PremiumPackageType } from '@/domain/enums/premiumPackages/PremiumPackageType';
import type { IdentityVerificationStatus } from '@/domain/enums/premiumPackages/IdentityVerificationStatus';
import type { RootState } from '@/presentation/store';
import {
  selectUserPremiumPackages,
  selectGetPremiumPackageStatus,
  selectGetIdentityVerificationStatus,
  selectHasPremiumPackageEvenIfProcessing,
} from '@/presentation/store/slices/premiumPackages/premiumPackagesSlice';

export interface PremiumPackagesUICallbacks {
  showSubscribeInfo: (pkg: PremiumPackageEntity) => Promise<unknown>;
  showRestoreInfo: () => Promise<unknown>;
  showVerifyInfo: () => Promise<unknown>;
  showIdentityInfo: () => Promise<unknown>;
}

export const usePremiumPackages = (uiCallbacks: PremiumPackagesUICallbacks) => {
  const [premiumPackages, setPremiumPackages] = useState<PremiumPackageEntity[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  // Subscribe at the top level so the hook re-renders when the Firestore
  // listener updates the slice. The selector callbacks below synthesize a
  // minimal RootState from this value to delegate to the slice's selectors,
  // which remain the single source of truth for the predicate logic.
  const userPremiumPackages = useSelector(selectUserPremiumPackages);

  const fetchPremiumPackages = useCallback(async () => {
    setIsLoading(true);
    const useCase = new GetPremiumPackagesUseCase();
    const packages = await useCase.execute();
    setPremiumPackages(packages);
    setIsLoading(false);
  }, []);

  const getPackageStatus = useCallback(
    (productId: string): InAppPurchaseStatus =>
      selectGetPremiumPackageStatus(
        { premiumPackages: { userPremiumPackages, isLoaded: true } } as unknown as RootState,
        productId,
      ),
    [userPremiumPackages],
  );

  const getIdentityVerificationStatus = useCallback(
    (): IdentityVerificationStatus | null =>
      selectGetIdentityVerificationStatus(
        { premiumPackages: { userPremiumPackages, isLoaded: true } } as unknown as RootState,
      ),
    [userPremiumPackages],
  );

  const isIdentityPackage = useCallback((productId: string): boolean => {
    return productId === PremiumPackageType.identityPro;
  }, []);

  const handleSubscribe = useCallback(async (pkg: PremiumPackageEntity) => {
    const hasOrProcessing = selectHasPremiumPackageEvenIfProcessing(
      { premiumPackages: { userPremiumPackages, isLoaded: true } } as unknown as RootState,
      pkg.productId,
    );
    if (hasOrProcessing) {
      return;
    }
    await uiCallbacks.showSubscribeInfo(pkg);
  }, [userPremiumPackages, uiCallbacks]);

  const handleRestorePurchases = useCallback(async () => {
    await uiCallbacks.showRestoreInfo();
  }, [uiCallbacks]);

  const handleGetVerified = useCallback(async () => {
    await uiCallbacks.showVerifyInfo();
  }, [uiCallbacks]);

  const handleIdentityInfo = useCallback(async () => {
    await uiCallbacks.showIdentityInfo();
  }, [uiCallbacks]);

  return {
    premiumPackages,
    isLoading,
    fetchPremiumPackages,
    getPackageStatus,
    getIdentityVerificationStatus,
    isIdentityPackage,
    handleSubscribe,
    handleRestorePurchases,
    handleGetVerified,
    handleIdentityInfo,
  };
};
